/**
 * HD (xBRZ-baked) battler skins for in-world duels — the Phase-B/C
 * half of the sample-first taste gate (owner ask 2026-08-03: "im
 * hoping we can compare with the upscaled at 4x" — live, in the duel,
 * on the phone). Swaps a battler sprite between its GBA OW sheet
 * (`follower-ow:<species>`, 32px frames) and the xBRZ-4× bake
 * (`follower-ow-hd:<species>`, 128px frames at scale 1/4 — same
 * world-px footprint, same 9-frame layout, same indices).
 *
 * DESIGN.md Zone 3 doctrine note: HD art in the world is an
 * exception, scoped to the duel camera only and gated by
 * DUEL_HD_BATTLERS_ENABLED in WorldScene until the owner rules.
 * The overworld follower/roamers outside a duel NEVER wear these.
 *
 * Pure + structurally typed so vitest covers it without Phaser.
 */

/** Bake factor of the .hd4 sheets (sprite-fetch --mode=hd). */
export const HD_SHEET_SCALE = 4;

const HD_PREFIX = 'follower-ow-hd:';
const OW_PREFIX = 'follower-ow:';
const STORAGE_KEY = 'poke-duel-hd';

export interface TextureSource {
  exists(key: string): boolean;
}

/** The subset of Phaser.GameObjects.Sprite the skin swap touches. */
export interface SkinnableSprite {
  active: boolean;
  texture: { key: string };
  frame: { name: string };
  setTexture(key: string, frame?: string | number): unknown;
  setScale(scale: number): unknown;
}

export function hdTextureKey(species: string): string {
  return `${HD_PREFIX}${species}`;
}

export function owTextureKey(species: string): string {
  return `${OW_PREFIX}${species}`;
}

/** Species carried by a battler's current texture key, HD or not —
 *  the toggle rethreads skins mid-battle without knowing what
 *  force-switches happened since staging. */
export function speciesFromKey(key: string): string | null {
  if (key.startsWith(HD_PREFIX)) return key.slice(HD_PREFIX.length);
  if (key.startsWith(OW_PREFIX)) return key.slice(OW_PREFIX.length);
  return null;
}

/** Resting sprite scale implied by a texture key. Texture-derived on
 *  purpose: the level-up pulse reads battlerBaseScale() at fire time,
 *  and deriving from the key is immune to both toggle races and
 *  mid-tween scale reads (a tracked field would go stale the moment
 *  the toggle bypasses its owner). */
export function restScaleOf(textureKey: string): number {
  return textureKey.startsWith(HD_PREFIX) ? 1 / HD_SHEET_SCALE : 1;
}

/** Desired skin for a species: the HD sheet at 1/4 when preferred AND
 *  baked (13-species roster — anything outside it stays GBA), the
 *  plain OW sheet at 1 otherwise. */
export function resolveOwSkin(
  textures: TextureSource,
  species: string,
  hd: boolean,
): { key: string; scale: number } {
  const key = hdTextureKey(species);
  if (hd && textures.exists(key)) return { key, scale: 1 / HD_SHEET_SCALE };
  return { key: owTextureKey(species), scale: 1 };
}

/** Point a battler at the desired skin variant, preserving the current
 *  frame index (identical layouts) and world-px footprint (128 × 1/4 =
 *  32). Missing target texture → no-op (keep the prior skin rather
 *  than flashing __MISSING — same posture as applySwitchVisual).
 *  Returns the resting scale actually in effect afterwards. */
export function applyOwSkin(
  textures: TextureSource,
  sprite: SkinnableSprite,
  species: string,
  hd: boolean,
): number {
  if (!sprite.active) return restScaleOf(sprite.texture.key);
  const skin = resolveOwSkin(textures, species, hd);
  if (!textures.exists(skin.key)) return restScaleOf(sprite.texture.key);
  if (sprite.texture.key !== skin.key) {
    const frame = Number.parseInt(sprite.frame.name, 10);
    sprite.setTexture(skin.key, Number.isFinite(frame) ? frame : 0);
  }
  sprite.setScale(skin.scale);
  return skin.scale;
}

/** Stored toggle preference — spans duels like the pinch zoom pref. */
export function hdPreferred(): boolean {
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setHdPreferred(on: boolean): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, on ? '1' : '0');
  } catch {
    // storage unavailable — the toggle still works this battle
  }
}
